import axios from "axios";
import { Box, Grid } from "@mui/material";
import localize from "../../../../common/localize";
import { EvidenceTypeEnum } from "../../../../common/utils";
import ReportSectionTitle from "../../../../components/common/ReportSectionTitle";
import ReportEvidenceItem from "./ReportEvidenceItem";
import { useEffect, useState } from "react";
import useGlobalContext from "../../../../hooks/useGlobalContext";

export default function ReportEvidenceData({
  eventId,
  evidences,
  reviews,
  original,
  isPanelReport,
  columnsForPanelReport,
  hideModificationDate,
}) {
  const { EVENTS_API, config } = useGlobalContext();

  const [evidenceSections, setEvidenceSections] = useState([]);

  const getEvidenceSections = async () => {
    const data = await axios.get(
      `${EVENTS_API}/${eventId}/evidence-section`,
      config,
    );

    if (data?.data?.length > 0) {
      setEvidenceSections([...data.data]);
      return;
    }

    setEvidenceSections([]);
  };

  useEffect(() => {
    getEvidenceSections();
  }, []);

  const evidencesList = (type) => {
    if (!evidences) {
      return null;
    }

    if (original) {
      return evidences?.filter(
        (x) =>
          x.EvidenceType !== EvidenceTypeEnum.ClientSignature &&
          x.EvidenceType !== EvidenceTypeEnum.PersonalSignature,
      );
    }

    if (!type) {
      return evidences?.filter(
        (x) =>
          x.Available === 1 &&
          x.EvidenceType !== EvidenceTypeEnum.ClientSignature &&
          x.EvidenceType !== EvidenceTypeEnum.PersonalSignature,
      );
    }

    if (type === EvidenceTypeEnum.None) {
      return evidences?.filter(
        (x) =>
          x.Available === 1 &&
          (x.EvidenceType === EvidenceTypeEnum.Video ||
            x.EvidenceType === EvidenceTypeEnum.Audio),
      );
    }

    return evidences?.filter(
      (x) =>
        x.Available === 1 &&
        x.EvidenceType === type &&
        x.EvidenceType !== EvidenceTypeEnum.ClientSignature &&
        x.EvidenceType !== EvidenceTypeEnum.PersonalSignature,
    );
  };

  const getFieldEvidences = (evidencesArray) => {
    if (evidencesArray?.length > 0) {
      return (
        <Box className="mt-2">
          <ReportSectionTitle title={localize.audio_and_video} allBorders />
          {evidencesArray.map((evidence) => (
            <div key={evidence.Id} className="w-full">
              <ReportEvidenceItem
                key={`reportEvidenceItem_${evidence?.Id}`}
                eventId={eventId}
                evidence={evidence}
                reviews={reviews}
                original={original}
                panelReport={false}
                hideModificationDate={hideModificationDate}
              />
            </div>
          ))}
        </Box>
      );
    }
  };

  function chunkArray(array, size) {
    return array.reduce((acc, item, index) => {
      const chunkIndex = Math.floor(index / size);

      if (!acc[chunkIndex]) {
        acc[chunkIndex] = [];
      }

      acc[chunkIndex].push(item);
      return acc;
    }, []);
  }

  const showEvidenceGroup = (evidencesGroup, gridSize) => {
    return evidencesGroup.map((group) => (
      <Box className="w-full print-row mt-2" key={Math.random()}>
        <Grid
          key={Math.random()}
          container
          spacing={2}
          className="w-full print-row"
        >
          {group.map((evidence) => (
            <Grid
              item
              sm={gridSize}
              key={`reportEvidenceItem_${evidence?.Id}_gridItem`}
            >
              <ReportEvidenceItem
                key={`reportEvidenceItem_${evidence?.Id}`}
                eventId={eventId}
                evidence={evidence}
                reviews={reviews}
                original={original}
                panelReport={isPanelReport}
                columns={columnsForPanelReport}
                hideModificationDate={hideModificationDate}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    ));
  };

  const getPanelReport = (evidencesArray) => {
    if (evidencesArray?.length > 0) {
      const gridSize =
        columnsForPanelReport == 1 ? 12 : columnsForPanelReport == 2 ? 6 : 4;

      let evidencesGroup = [];

      if (evidenceSections && evidenceSections?.length > 0) {
        const sectionsEvidences = evidenceSections.map((section) => (
          <Box className="mt-2">
            <ReportSectionTitle
              key={section.Id}
              title={section.Name}
              allBorders
            />

            {section.Description && (
              <div className="w-full m-2">
                <label className="report_little_text">
                  <b>{localize.description}: </b>
                  {section.Description}
                </label>
              </div>
            )}

            {showEvidenceGroup(
              chunkArray(
                evidencesArray.filter((x) => x.Section?.Id === section.Id),
                columnsForPanelReport,
              ),
              gridSize,
            )}
          </Box>
        ));

        const generalEvidences = evidencesArray.filter(
          (x) => !x.Section || !x.Section?.RegisterStatus === 0,
        );
        return (
          <>
            {sectionsEvidences}
            <Box className="mt-2">
              <ReportSectionTitle title={localize.general} allBorders />
              {showEvidenceGroup(
                chunkArray(generalEvidences, columnsForPanelReport),
                gridSize,
              )}
            </Box>
          </>
        );
      }

      evidencesGroup = chunkArray(evidencesArray, columnsForPanelReport);
      return (
        <>
          <Box className="mt-2">
            <ReportSectionTitle title={localize.general} allBorders />
            {showEvidenceGroup(evidencesGroup, gridSize)}
          </Box>
        </>
      );
    }
  };

  return (
    <div>
      <div key={Math.random()} className="page-break" />
      <ReportSectionTitle title={localize.evidences} />
      <div
        className="report_section_without_top_border flex flex-col gap-2"
        style={{ paddingBottom: "10px" }}
      >
        <label
          className="report_little_text m-2"
          style={{
            paddingLeft: "5px",
            paddingRight: "5px",
          }}
        >
          {localize.evidences_description}
        </label>

        <div className={isPanelReport ? "evidence_section_background p-2" : ""}>
          {isPanelReport
            ? getPanelReport(evidencesList(EvidenceTypeEnum.Image))
            : getFieldEvidences(evidencesList())}
        </div>
        <div>
          {isPanelReport &&
            getFieldEvidences(evidencesList(EvidenceTypeEnum.None))}
        </div>
      </div>
    </div>
  );
}
